"use client";

import { SubmitButton } from "@/components/ui/button";
import { Icon, type IconName } from "@/components/ui/icon";
import { StepFooter, StepScrollArea } from "@/components/wizard";
import { pluralize } from "@/lib/string/pluralize";
import { cn } from "@/lib/utils";

import { useExportWizard } from "./context";

export function StepFormat() {
  const {
    resolvedCount,
    formats,
    creditsPerProfilePerFormat,
    toggleFormat,
    setStep,
  } = useExportWizard();

  return (
    <>
      <StepScrollArea contentClassName="gap-4">
        <p className="text-sm text-muted-foreground">
          Choose one or both export formats for{" "}
          <span className="font-medium text-foreground">
            {resolvedCount} {pluralize(resolvedCount, "profile")}
          </span>
          .
        </p>
        <div
          role="group"
          aria-label="Export formats"
          className="flex flex-col gap-2"
        >
          <FormatCard
            icon="IconFileText"
            label="PDF file(s)"
            description="Individual profile PDFs, ideal for sharing"
            creditNote={`${creditsPerProfilePerFormat} ${pluralize(creditsPerProfilePerFormat, "credit")}/profile`}
            selected={formats.includes("pdf")}
            onClick={() => toggleFormat("pdf")}
          />
          <FormatCard
            icon="IconFileDownload"
            label="CSV file"
            description="Spreadsheet format with all profile data"
            creditNote={`${creditsPerProfilePerFormat} ${pluralize(creditsPerProfilePerFormat, "credit")}/profile`}
            selected={formats.includes("csv")}
            onClick={() => toggleFormat("csv")}
          />
        </div>
        <p className="text-xs text-muted-foreground tabular-nums">
          {formats.length === 0
            ? "Select at least one format to continue."
            : formats.length === 2
              ? `Both formats selected — ${2 * creditsPerProfilePerFormat} export credits per profile.`
              : `${creditsPerProfilePerFormat} export ${pluralize(creditsPerProfilePerFormat, "credit")} per profile.`}
        </p>
      </StepScrollArea>
      <StepFooter>
        <span />
        <SubmitButton
          type="button"
          size="sm"
          onClick={() => setStep(2)}
          disabled={formats.length === 0}
          className="w-full bg-[#FFAF53] border-[#e89a3a] text-black hover:bg-[#F5A347] active:bg-[#E8993A] sm:w-auto"
        >
          Continue
        </SubmitButton>
      </StepFooter>
    </>
  );
}

export function FormatCard({
  icon,
  label,
  description,
  creditNote,
  selected,
  onClick,
}: {
  icon: IconName;
  label: string;
  description: string;
  creditNote: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={selected}
      onClick={onClick}
      className={cn(
        "w-full rounded-lg border p-3 flex items-center gap-3 transition-colors cursor-pointer",
        "focus-visible:outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50",
        selected
          ? "border-[#FFAF53] bg-[#FFAF53]/5"
          : "border-border bg-contrast/[0.02] hover:bg-contrast/4 hover:border-foreground/15",
      )}
    >
      <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-[#FFAF53]/10">
        <Icon
          name={icon}
          size={16}
          className="text-[#c9682a] dark:text-[#FFAF53]"
        />
      </div>
      <div className="flex flex-col items-start text-left">
        <span className="text-sm font-medium text-foreground">{label}</span>
        <span className="text-xs text-muted-foreground">{description}</span>
      </div>
      <div className="ml-auto flex shrink-0 items-center gap-2.5">
        <span className="text-[11px] text-muted-foreground tabular-nums">
          {creditNote}
        </span>
        <div
          className={cn(
            "flex size-4 shrink-0 items-center justify-center rounded-[4px] border transition-colors",
            selected ? "border-[#FFAF53] bg-[#FFAF53]" : "border-contrast/25",
          )}
        >
          {selected && (
            <Icon name="IconCheckmark1" size={11} className="text-black" />
          )}
        </div>
      </div>
    </button>
  );
}
